"use client";

import { c as _c } from "react/compiler-runtime";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { useIsNodeSelectedByOthers } from "@/hooks/useNodeSelectionPresence";
import { AnimatePresence, motion } from "motion/react";
interface NodeSelectionIndicatorProps {
  /** The node ID to check for other users' selections */
  nodeId: string;
  /** Optional class name for positioning */
  className?: string;
}

/**
 * Shows a visual indicator when other users have this node selected.
 * Displays colored avatars of users who have the node selected.
 *
 * Usage:
 * ```tsx
 * <div className="relative">
 *   <NodeSelectionIndicator nodeId={id} />
 *   {/* rest of node content *\/}
 * </div>
 * ```
 */
export default function NodeSelectionIndicator(t0) {
  const $ = _c(14);
  const {
    nodeId,
    className
  } = t0;
  const selections = useIsNodeSelectedByOthers(nodeId);
  if (selections.length === 0) {
    return null;
  }
  const t1 = `absolute -right-2 -top-2 z-50 flex items-center gap-0.5 ${className || ""}`;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      scale: 0,
      opacity: 0
    };
    t3 = {
      scale: 1,
      opacity: 1
    };
    t4 = {
      scale: 0,
      opacity: 0
    };
    t5 = {
      type: "spring",
      stiffness: 500,
      damping: 30
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
    $[3] = t5;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
    t5 = $[3];
  }
  let t6;
  if ($[4] !== selections) {
    let t7;
    if ($[6] !== selections.length) {
      t7 = (selection, index) => {
        const isAnonymous = selection.clientId.startsWith("anon_") || selection.clientId.startsWith("client-") || selection.clientId.startsWith("anon-");
        return <motion.div key={selection.clientId} className="dark:bg-ds-neutral-900/80 dark:ring-ds-neutral-700/70 shadow-ds-elev-2 relative overflow-hidden rounded-full bg-white/80 ring-2 ring-white/70 backdrop-blur-md" style={{
          marginLeft: index > 0 ? "-6px" : 0,
          zIndex: selections.length - index
        }} initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          delay: index * 0.05
        }}><UserAvatar b3GlobalId={isAnonymous ? undefined : selection.b3GlobalId} isAnonymous={isAnonymous} width={24} height={24} /><div className="absolute inset-0 animate-ping rounded-full opacity-30" style={{
            backgroundColor: selection.color
          }} /></motion.div>;
      };
      $[6] = selections.length;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    t6 = selections.slice(0, 3).map(t7);
    $[4] = selections;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[8] !== selections.length) {
    t7 = selections.length > 3 && <div className="dark:bg-ds-neutral-700/90 text-3xs flex h-5 w-5 items-center justify-center rounded-full bg-zinc-600/90 font-medium text-white ring-2 ring-white/70 backdrop-blur-md dark:ring-zinc-600/70" style={{
      marginLeft: "-6px"
    }}>+{selections.length - 3}</div>;
    $[8] = selections.length;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t1 || $[11] !== t6 || $[12] !== t7) {
    t8 = <AnimatePresence><motion.div className={t1} initial={t2} animate={t3} exit={t4} transition={t5}>{t6}{t7}</motion.div></AnimatePresence>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}

/**
 * A ring/border indicator that wraps around the node when selected by others.
 * Use this as an alternative to the avatar indicator for a subtler effect.
 */
export function NodeSelectionRing(t0) {
  const $ = _c(7);
  const {
    nodeId
  } = t0;
  const selections = useIsNodeSelectedByOthers(nodeId);
  if (selections.length === 0) {
    return null;
  }
  const color = selections[0].color;
  const t1 = `0 0 0 2px ${color}, 0 0 12px ${color}40`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      boxShadow: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 0
    };
    t4 = {
      opacity: 1
    };
    t5 = {
      opacity: 0
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t2) {
    t6 = <motion.div className="pointer-events-none absolute inset-0 rounded-xl" style={t2} initial={t3} animate={t4} exit={t5} />;
    $[5] = t2;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}